import { useLocale } from '@solinkcorp/locale';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ProfileIcon,
} from '@solinkcorp/solink-ui';
import { ChevronDown, LogOut, Pencil } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ICON_SIZE, SDM_CONFIG_ROUTE, SDM_EVENTS } from '../../../constants';
import { useSDMAnalytics } from '../../../contexts/AnalyticsContext';
import styles from '../../../sdm.module.css';
import DarkModeMenuItem from '../../DarkMode/DarkModeMenuItem';
import { sdmCancelBtnStyles, sdmOutlineBtnStyles } from '../LayoutSelector/LayoutSelectorStyles';
import { VerticalDivider } from './Header';

const LOGO_SIZE = 34;

interface HeaderRightProps {
  isEditing: boolean;
  hasMaxWidgets: boolean;
  addAction: () => void;
  cancelAction: () => void;
  saveAction: () => void;
  canEdit: boolean;
  user: string;
}

const HeaderRight: React.FC<HeaderRightProps> = ({
  isEditing,
  hasMaxWidgets,
  addAction,
  cancelAction,
  saveAction,
  canEdit,
  user,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const locale = useLocale();
  const location = useLocation();
  const analytics = useSDMAnalytics();

  const onLogout = () => {
    navigate('/logout');
  };

  const editLayoutAction = useCallback(() => {
    analytics.trackSDMEvent(SDM_EVENTS.EDIT_LAYOUT, { description: 'Clicked edit layout' });
    navigate(`${SDM_CONFIG_ROUTE}${location.search}`);
  }, [navigate, location.search, analytics]);

  return (
    <span className={styles.buttonBar}>
      {isEditing ? (
        <>
          <Button
            variant={'outline'}
            className={sdmOutlineBtnStyles}
            onClick={addAction}
            disabled={hasMaxWidgets}
            data-testid="add-sdm-widget-button"
          >
            {locale.serviceDisplayMonitor.addMetric}
          </Button>
          <VerticalDivider />
          <Button
            variant={'outline'}
            onClick={cancelAction}
            className={sdmCancelBtnStyles}
            data-testid="cancel-sdm-layout-button"
          >
            {locale.general.cancel}
          </Button>
          <Button
            variant={'default'}
            className="text-interaction-primary-content-default"
            onClick={saveAction}
            data-testid="save-sdm-layout-button"
          >
            {locale.general.save}
          </Button>
        </>
      ) : (
        <>
          {canEdit && (
            <Button
              onClick={editLayoutAction}
              variant="default"
              className="text-interaction-primary-content-default"
              data-testid="edit-sdm-layout-button"
            >
              <Pencil size={ICON_SIZE} />
              {locale.general.edit}
            </Button>
          )}
          <VerticalDivider />
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger
              className="inline-flex items-center gap-1 rounded-full !px-0 !py-0"
              data-testid="sdm-profile-menu-trigger"
            >
              <ProfileIcon identifier={user} size={LOGO_SIZE} />
              <ChevronDown
                className={`text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                size={ICON_SIZE}
              />
              <span className="sr-only">{user}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={cn('w-40 border-border-muted')}
              align="end"
              side="bottom"
              sideOffset={5}
            >
              <DarkModeMenuItem />
              <DropdownMenuItem onClick={onLogout} data-testid="sdm-logout">
                <LogOut size={ICON_SIZE} />
                {locale.auth.logout}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </span>
  );
};

export default HeaderRight;
